import { TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { DropzoneArea } from "material-ui-dropzone";
import { useDataProvider, useNotify } from "ra-core";
import React from "react";
import { deliveryActions } from "../util";

interface Props {
  selectedIds?: number[];
  onComplete?: () => void;
}

const EndMultipleDeliveryDialog: React.FC<Props> = (props) => {
  const dataProvider = useDataProvider();
  const { selectedIds, onComplete } = props;
  const [open, setOpen] = React.useState(false);
  const [deliveryNote, setDeliveryNote] = React.useState("");
  const [deliveredImages, setDeliveredImages] = React.useState<
    { url: string; type: string }[]
  >([]);
  const [endableDeliveries, setEndableDeliveries] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const notify = useNotify();
  const deliveredTime = new Date().toISOString();

  const handleClose = () => {
    setOpen(false);
    setDeliveryNote("");
    setDeliveredImages([]);
  };

  const convertFileToBase64 = (file: File) => {
    var reader = new FileReader();
    return new Promise((resolve) => {
      reader.readAsDataURL(file);
      reader.onloadend = function () {
        var base64data = reader.result;
        resolve(base64data);
      };
    });
  };

  const handleSelectFile = async (files: File[]) => {
    // convert file to base64
    const convertAll = async (files: File[]) =>
      Promise.all(
        files.map(async (file: File) => {
          return await convertFileToBase64(file);
        })
      );

    convertAll(files)
      .then((base64s) =>
        base64s.map((base64) => ({
          url: base64,
          type: "image",
        }))
      )
      .then((delivered_images) =>
        setDeliveredImages(delivered_images as { url: string; type: string }[])
      );
  };

  const handleAccept = async () => {
    if (endableDeliveries.length === 0) {
      notify("No deliveries can be ended", "warning");
      return;
    }

    setIsLoading(true);

    try {
      // End each delivery individually with their target_location_id as delivered_location_id
      const updatePromises = endableDeliveries.map((delivery) => {
        return dataProvider.update("deliveries", {
          id: delivery.id,
          data: {
            action: deliveryActions.FINISH,
            delivered_time: deliveredTime,
            delivered_location_id: delivery.target_location_id,
            delivered_note: deliveryNote,
            delivered_images: deliveredImages,
          },
          previousData: delivery,
        });
      });

      await Promise.all(updatePromises);

      notify(
        `Successfully completed ${endableDeliveries.length} deliveries.`,
        "info"
      );
      setOpen(false);
      setDeliveryNote("");
      setDeliveredImages([]);
      onComplete?.(); // Refresh and clear selection
    } catch (error: any) {
      notify(`Error: ${error.message}`, "warning");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeliveryNoteChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDeliveryNote(event.target.value);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;

  const handleOpenDialog = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify("Please select deliveries first", "warning");
      return;
    }

    try {
      // Fetch complete delivery data to get target_location_id
      const deliveryPromises = selectedIds.map((id) =>
        dataProvider.getOne("deliveries", { id })
      );
      const deliveryResponses = await Promise.all(deliveryPromises);
      const deliveries = deliveryResponses.map((response) => response.data);

      setEndableDeliveries(deliveries);
      setOpen(true);
    } catch (error: any) {
      notify(`Error loading delivery data: ${error.message}`, "warning");
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpenDialog}
        size={"small"}
        disabled={!hasSelection}
      >
        End Delivery {hasSelection ? `(${selectedIds.length})` : ""}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">
          End Multiple Deliveries
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {endableDeliveries.length > 0 ? (
              <>
                You have <strong>{endableDeliveries.length} deliveries</strong>{" "}
                that can be completed.
                {selectedIds &&
                  selectedIds.length > endableDeliveries.length && (
                    <>
                      {" "}
                      ({selectedIds.length - endableDeliveries.length}{" "}
                      deliveries were filtered out as they are already
                      verified.)
                    </>
                  )}
                <br />
                All selected deliveries will be marked as delivered at the
                current time.
              </>
            ) : (
              <>
                No deliveries can be ended. Verified deliveries cannot be ended
                again.
              </>
            )}
          </DialogContentText>

          <TextField
            margin="dense"
            id="delivery-note"
            label="Delivery Note (Optional)"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={deliveryNote}
            onChange={handleDeliveryNoteChange}
            style={{ marginTop: 16, marginBottom: 16 }}
          />

          <DropzoneArea filesLimit={10} onChange={handleSelectFile} />

          {deliveredImages.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>Selected files: {deliveredImages.length}</strong>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            color="primary"
            disabled={endableDeliveries.length === 0 || isLoading}
          >
            {isLoading
              ? "Completing..."
              : `Complete ${endableDeliveries.length} Deliveries`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EndMultipleDeliveryDialog;
